import type React from 'react';

import type { RowData, Table, TableOptions } from '@tanstack/react-table';
import {
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { WithDataTestid } from '@vlabs/uikit/types';
import classNames from 'classnames';

import type { TableViewBodyProps } from './body';
import { TableViewBody } from './body';
import type { TableViewHeadProps } from './head';
import { TableViewHead } from './head';
import { buildTestid } from '../../lib/utils';

import './TableView.sass';

export type TBulkActions<TData extends RowData> = ((table: Table<TData>) => React.ReactNode);

/**
 * Makes a set of properties K in type T optional, while keeping other properties required.
 */
type MakePartial<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export interface TableViewProps<TData extends RowData> extends
  WithDataTestid,
  MakePartial<TableOptions<TData>, 'getCoreRowModel'> {
  className?: string;
  TableHead?: React.JSXElementConstructor<TableViewHeadProps<TData>>;
  TableBody?: React.JSXElementConstructor<TableViewBodyProps<TData>>;
}

const displayName = 'TableView';
const testid = buildTestid(displayName);

function TableView<TData extends RowData>({
  data,
  columns,
  getCoreRowModel: getCoreRowModelProp = undefined,
  className = undefined,
  TableHead = TableViewHead,
  TableBody = TableViewBody,
  'data-testid': dataTestId = undefined,
  ...tableProps
}: TableViewProps<TData>): React.JSX.Element {
  const table = useReactTable<TData>({
    columns,
    data,
    getCoreRowModel: getCoreRowModelProp ?? getCoreRowModel(),
    ...tableProps,
  });

  return (
    <div
      className={classNames('TableView', className)}
      {...testid(dataTestId)}
    >
      <div className="TableView__Inner">
        <TableHead
          data-testid={dataTestId}
          table={table}
        />
        <TableBody
          data-testid={dataTestId}
          table={table}
        />
      </div>
    </div>
  );
}

TableView.displayName = displayName;

export { TableView };
